import { api } from "@/api/client";

/**
 * Дашборд склада: что лежит на складах СЕЙЧАС.
 *
 * Данные живые — один запрос ходит в 1С под подключением пользователя, кеша на
 * бэкенде нет, и `as_of` в ответе значит «на этот момент». Поэтому экран
 * показывает время среза рядом с кнопкой «Обновить»: число без времени на
 * дашборде неотличимо от вчерашнего.
 *
 * Типы повторяют `backend/src/wh_helper/warehouse_dashboard/schemas.py` 1:1 —
 * своих полей фронт не придумывает и чужие не переименовывает.
 */

/** Остаток на одной структурной единице. `kind` — `Склад`/`Подразделение`:
 *  остаток бывает и на подразделении, и без типа две строки неразличимы. */
export interface WarehouseQty {
  ref: string;
  name: string;
  kind: string;
  /** Базовые единицы учёта РАЗНЫХ товаров (шт, кг, л) — сумма условная. */
  qty: number;
  /** Себестоимость остатка, ₽ (учётный `Запасы`); `null` — у пары нет строки
   *  в `Запасы` (это не ноль, на экране «—»). */
  cost_rub: number | null;
}

export interface OrganizationQty {
  ref: string;
  name: string;
  qty: number;
  /** Сумма `cost_rub` складов, у которых он есть; `null` — ни у одного. */
  cost_rub: number | null;
  warehouses: WarehouseQty[];
}

/** Корзина ОСГ: подпись и границы в процентах. `null` там, где границы нет: у
 *  «просрочено» нет нижней (`pct_from`), верхняя же осмысленная — `pct_to: 0`,
 *  «всё, что ниже нуля»; у «без данных о сроке» нет обеих. Каталог и его
 *  ПОРЯДОК приходят с бэкенда — фронт рисует шкалу как есть и не сортирует её. */
export interface ShelfLifeBucket {
  key: string;
  title: string;
  pct_from: number | null;
  pct_to: number | null;
}

export interface DirectionShelfLife {
  direction: string;
  warehouse_ref: string;
  warehouse_name: string;
  total: number;
  /** Ключ корзины → штуки. Корзины без остатка в словаре ОТСУТСТВУЮТ — это ноль,
   *  а не пропуск: сумма значений равна `total`. */
  buckets: Record<string, number>;
}

export interface OrganizationShelfLife {
  ref: string;
  name: string;
  directions: DirectionShelfLife[];
}

/** Юрлицо, скрытое из «Сроков годности» настройкой. Приходит ЦЕЛИКОМ: вкладка
 *  пишет «Скрыто настройкой: … (N шт)», а «Обзор» считает его просрочку дальше. */
export interface HiddenShelfLife extends OrganizationShelfLife {
  /** Сумма `total` его складов МП/ОПТ, шт. */
  total: number;
}

export interface ShelfLife {
  buckets: ShelfLifeBucket[];
  organizations: OrganizationShelfLife[];
  /** Скрытые настройкой юрлица — в `organizations` их нет. */
  hidden: HiddenShelfLife[];
}

export interface DashboardSummary {
  /** ISO-момент среза (UTC, с таймзоной). */
  as_of: string;
  total_qty: number;
  /** Сумма `cost_rub` дерева; ₽ вне дерева (контрагенты, склады без физики)
   *  сюда не входят и названы в `warnings`. */
  total_cost_rub: number;
  organizations: OrganizationQty[];
  shelf_life: ShelfLife;
  /** Незаполненное направление склада, ненайденное имя — картинка неполная, и
   *  молча показанная неполная картинка выглядит как полная. */
  warnings: string[];
}

/** Ключ корзины «просрочено» (она красится иначе, чем проценты; число в ней —
 *  красным). */
export const BUCKET_EXPIRED = "expired";

/** Корзины «срок 0–10 %» — «Обзор» складывает их в один пункт «Требует
 *  внимания». Ключи сверяются с каталогом ответа: нет ключа — пункт об этом,
 *  а не молчаливый ноль (`lib/warehouse-overview.ts::sumBuckets`). */
export const LOW_SHELF_LIFE_BUCKETS = ["0-5", "5-10"] as const;

/** Цветовая метка корзины ОСГ: точка у подписи и полоска доли в ячейке. */
export interface BucketTone {
  dot: string;
  bar: string;
}

const TONE_FRESH: BucketTone = { dot: "bg-emerald-500", bar: "bg-emerald-500" };
const TONE_MIDDLE: BucketTone = { dot: "bg-emerald-300", bar: "bg-emerald-300" };
const TONE_LOW: BucketTone = { dot: "bg-amber-500", bar: "bg-amber-500" };
const TONE_EXPIRED: BucketTone = { dot: "bg-rose-500", bar: "bg-rose-500" };
const TONE_UNKNOWN: BucketTone = { dot: "bg-slate-300", bar: "bg-slate-400" };

/** Цвет корзины — ТОЛЬКО по её ключу, ОДНА карта (как подписи направлений).
 *  Шкала: 50–100 % — свежее (зелёный), 10–50 % — бледно-зелёный, 0–10 % —
 *  янтарь (скоро выйдет срок), просрочено — красный. «Без данных о сроке» и
 *  НЕИЗВЕСТНЫЙ ключ — серые: новая корзина с бэкенда покажется как есть, а не
 *  выдуманным цветом. Порядок и границы корзин фронт не знает и не считает. */
const BUCKET_TONES: Record<string, BucketTone> = {
  "90-100": TONE_FRESH,
  "80-90": TONE_FRESH,
  "70-80": TONE_FRESH,
  "60-70": TONE_FRESH,
  "50-60": TONE_FRESH,
  "40-50": TONE_MIDDLE,
  "30-40": TONE_MIDDLE,
  "20-30": TONE_MIDDLE,
  "10-20": TONE_MIDDLE,
  "5-10": TONE_LOW,
  "0-5": TONE_LOW,
  [BUCKET_EXPIRED]: TONE_EXPIRED,
};

export function bucketTone(key: string): BucketTone {
  return BUCKET_TONES[key] ?? TONE_UNKNOWN;
}

/** Полоса списка «Просрочка и риск» — ключ `band` ответа `/expiry-risk`. */
export type ExpiryBand = "expired" | "lt10" | "lt20" | "lt30";

/** Цвет полосы риска: плитка (фон/рамка/текст) и точка. */
export interface BandTone {
  tile: string;
  text: string;
  dot: string;
}

/** Цвет полосы риска — ТОЛЬКО по ключу, ОДНА карта (рядом с `bucketTone`):
 *  просрочка — красный, «< 10 %» — янтарь (как корзины 0–10 %), 10–30 % —
 *  светлый янтарь. Неизвестный ключ — серый, как у корзин. */
const BAND_TONES: Record<string, BandTone> = {
  expired: { tile: "border-rose-200 bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
  lt10: { tile: "border-amber-300 bg-amber-50", text: "text-amber-800", dot: "bg-amber-500" },
  lt20: { tile: "border-amber-200 bg-amber-50/40", text: "text-amber-700", dot: "bg-amber-300" },
  lt30: { tile: "border-amber-200 bg-amber-50/40", text: "text-amber-700", dot: "bg-amber-300" },
};
const BAND_TONE_UNKNOWN: BandTone = {
  tile: "border-slate-200 bg-slate-50",
  text: "text-slate-700",
  dot: "bg-slate-300",
};

export function bandTone(band: string): BandTone {
  return BAND_TONES[band] ?? BAND_TONE_UNKNOWN;
}

/** Подписи направлений — ОДНО место, как у маркетплейсов в `api/returns.ts`.
 *  Тернарники по коду в разметке запрещены: третье направление добавилось бы
 *  мимо них. Неизвестный код показываем как есть — это видно, в отличие от
 *  пустой ячейки. */
const DIRECTION_TITLES: Record<string, string> = {
  mp: "МП",
  opt: "Опт",
};

export function directionTitle(direction: string): string {
  return DIRECTION_TITLES[direction] ?? direction;
}

export async function getWarehouseDashboardSummary(): Promise<DashboardSummary> {
  const { data } = await api.get<DashboardSummary>("/warehouse-dashboard/summary");
  return data;
}

/** Позиция ячейки ОСГ: номенклатура × характеристика × партия. Нет имени в
 *  справочнике — в поле стоит GUID, а причина в `warnings` ответа. Пустая
 *  характеристика/партия — пустая строка (их у позиции нет).
 *  `characteristic_ref`/`batch_ref` — сырые ссылки, для React-ключа строки
 *  (`characteristic`/`batch` — имя, не уникально: две партии могут называться
 *  одинаково). */
export interface BucketItem {
  nomenclature_ref: string;
  article: string;
  name: string;
  characteristic: string;
  characteristic_ref: string;
  batch: string;
  batch_ref: string;
  /** ISO-дата `YYYY-MM-DD` или `null`. */
  produced: string | null;
  expires: string | null;
  /** Только у процентных корзин; у просрочки и «без данных» — `null`. */
  shelf_life_pct: number | null;
  qty: number;
}

/** Позиции одной ячейки ОСГ. `total` равен числу в ячейке сводки — корзину
 *  бэкенд считает той же функцией. */
export interface BucketItems {
  as_of: string;
  organization_name: string;
  warehouse_name: string;
  bucket: ShelfLifeBucket;
  total: number;
  items: BucketItem[];
  warnings: string[];
}

/** Адрес ячейки: `organization_ref` бывает ПУСТЫМ — так сводка отдаёт остаток
 *  «(без организации)», и бэкенд принимает пустую строку ровно как этот адрес. */
export interface BucketCellRef {
  organization_ref: string;
  warehouse_ref: string;
  bucket: string;
}

export async function getShelfLifeBucketItems(cell: BucketCellRef): Promise<BucketItems> {
  const { data } = await api.get<BucketItems>("/warehouse-dashboard/shelf-life/items", {
    params: cell,
  });
  return data;
}

// --- «Операции склада» (`GET /operations`, `operations.py`) -------------------
// Источник — БД ПОРТАЛА, не 1С: в цифрах только работа через портал (оговорка
// приходит в `notes`). Показатели фронт НЕ пересчитывает — ни дельт, ни сумм:
// рисует `values` как есть, `0` и `null` различает.

/** Календарная неделя пн–вс по Москве; `start`/`end` — ISO-даты `YYYY-MM-DD`.
 *  `partial` — текущая, неполная (последняя в списке). */
export interface OperationsWeek {
  start: string;
  end: string;
  partial: boolean;
}

/** Единица показателя: `count` — штук событий (что считается — в `title`),
 *  `units` — штук товара, `pallets`, `hours`, `share` — доля 0..1, `rub`. */
export type OperationsUnit = "count" | "units" | "pallets" | "hours" | "share" | "rub";

/** Показатель по неделям: `values[i]` относится к `weeks[i]`. `0` — процесс в
 *  неделю не работал, `null` — не определён (медиана пустого, доля от нуля).
 *  `unit` типизирован строкой, как в схеме: незнакомая единица с бэкенда
 *  показывается как есть (`fmtOperationValue`), а не ломает экран. */
export interface OperationsMetric {
  key: string;
  group: string;
  title: string;
  unit: string;
  values: (number | null)[];
  /** Куда рост — хорошо (`up`/`down`), `null` — не оценивается. Экран
   *  направление цветом НЕ красит. */
  better: string | null;
}

/** Очередь «сейчас». `oldest_at` — момент входа самого старого (у просрочек —
 *  прошедший срок, у незавершённого — открытие сессии), `null` — очередь пуста.
 *  Незавершённые сборки/приёмки возвратов разделены бэкендом на «в работе» и
 *  «зависшие» (без активности > 48 ч, `operations.STALE_AFTER`). */
export interface OperationsQueue {
  key: string;
  group: string;
  title: string;
  count: number;
  unit: string;
  oldest_at: string | null;
}

export interface Operations {
  /** ISO-момент расчёта (UTC, с таймзоной). */
  as_of: string;
  weeks: OperationsWeek[];
  metrics: OperationsMetric[];
  queues: OperationsQueue[];
  notes: string[];
}

/** Сколько недель показывает вкладка (решение владельца: тренд 8 недель). */
export const OPERATIONS_WEEKS = 8;

/** Подписи групп показателей — ОДНО место (как `directionTitle`). Неизвестный
 *  ключ показываем как есть: новая группа с бэкенда видна, а не пропадает. */
const OPERATIONS_GROUP_TITLES: Record<string, string> = {
  shipment: "Отгрузка",
  order_check: "Сборка заказов",
  receiving: "Приёмка",
  transfer_check: "Перемещения",
  fbs: "ФБС WB",
  ozon_fbs: "ФБС Ozon",
  returns: "Возвраты",
  inventory: "Инвентаризация",
};

export function operationsGroupTitle(group: string): string {
  return OPERATIONS_GROUP_TITLES[group] ?? group;
}

/** Очереди-просрочки: ненулевые — красным акцентом. Нулевые не прячутся. */
const OVERDUE_QUEUES: ReadonlySet<string> = new Set(["fbs_overdue", "ozon_overdue"]);

export function isOverdueQueue(key: string): boolean {
  return OVERDUE_QUEUES.has(key);
}

/** Очереди «зависших» сессий (без активности > 48 ч) — янтарём, приглушённо:
 *  это не работа, а брошенное, которое надо закрыть или удалить. */
const STALE_QUEUES: ReadonlySet<string> = new Set(["orders_stale", "returns_stale"]);

export function isStaleQueue(key: string): boolean {
  return STALE_QUEUES.has(key);
}

/** Подпись порога «зависшей» — одна на экран (порог — `operations.STALE_AFTER`). */
export const STALE_QUEUE_HINT = "без активности > 48 ч";

export async function getWarehouseOperations(weeks: number): Promise<Operations> {
  const { data } = await api.get<Operations>("/warehouse-dashboard/operations", {
    params: { weeks },
  });
  return data;
}

// --- «Отгружено со склада» (`GET /shipments`, `shipments.py`) ----------------
// Источник — 1С (расходные накладные по ВСЕМ юрлицам), а не портал: через
// портальную отгрузку проходит лишь часть расходных. Типы — 1:1 со
// `ShipmentsOut`/`ShipmentsOrganizationOut` из `schemas.py`.

/** Отгрузка одного юрлица по неделям: `units[i]`/`cost_rub[i]` — `weeks[i]`. */
export interface ShipmentsOrganization {
  ref: string;
  name: string;
  units: number[];
  cost_rub: number[];
}

/** Отгрузка по неделям пн–вс МСК. `cost_rub` — СЕБЕСТОИМОСТЬ (`Сумма`
 *  регистра `Запасы`), не выручка; `documents` — число различных расходных. */
export interface Shipments {
  /** ISO-момент расчёта (UTC, с таймзоной). */
  as_of: string;
  weeks: OperationsWeek[];
  units: number[];
  cost_rub: number[];
  documents: number[];
  by_organization: ShipmentsOrganization[];
  /** Не учтённое не-складское, сторно, отгрузка без организации. */
  warnings: string[];
}

export async function getWarehouseShipments(weeks: number): Promise<Shipments> {
  const { data } = await api.get<Shipments>("/warehouse-dashboard/shipments", {
    params: { weeks },
  });
  return data;
}

// --- позиции склада «Остатков» (`GET /stock/items`, `stock_items.py`) ----------
// Грузится при раскрытии склада. Типы — 1:1 со `StockItemsOut`/`StockItemOut`/
// `StockBatchOut` из `schemas.py`.

/** Партия позиции. Пустой `batch_ref` — остаток без партии у позиции, где есть
 *  и партионный. `bucket` — та же корзина ОСГ, что в сводке (`bucketTone`). */
export interface StockBatch {
  batch_ref: string;
  batch: string;
  /** ISO-дата `YYYY-MM-DD` или `null`. */
  produced: string | null;
  expires: string | null;
  shelf_life_pct: number | null;
  bucket: string;
  qty: number;
  /** ₽ строк `Запасы` этой партии; `null` — строки нет (не ноль). */
  cost_rub: number | null;
}

/** Позиция склада: номенклатура × характеристика. `batches` пуст — у позиции
 *  только нулевая партия, раскрывать нечего. */
export interface StockItem {
  nomenclature_ref: string;
  characteristic_ref: string;
  article: string;
  name: string;
  characteristic: string;
  qty: number;
  /** ВСЕ строки `Запасы` позиции на паре; `null` — ни одной. */
  cost_rub: number | null;
  batches: StockBatch[];
}

/** Позиции склада. `total_qty`/`total_cost_rub` равны ячейке склада сводки;
 *  ₽ позиций без физики в `items` нет — они названы в `warnings`. */
export interface StockItems {
  as_of: string;
  organization_name: string;
  warehouse_name: string;
  total_qty: number;
  total_cost_rub: number | null;
  items: StockItem[];
  warnings: string[];
}

/** Адрес склада: `organization_ref` бывает ПУСТЫМ — «(без организации)». */
export interface StockPairRef {
  organization_ref: string;
  warehouse_ref: string;
}

/** Ключ react-query позиций склада; префикс — для «Обновить» всей вкладки. */
export const STOCK_ITEMS_QUERY = "warehouse-dashboard-stock-items";

export async function getStockItems(pair: StockPairRef): Promise<StockItems> {
  const { data } = await api.get<StockItems>("/warehouse-dashboard/stock/items", {
    params: pair,
  });
  return data;
}

// --- «Просрочка и риск»: партии с истёкшим / истекающим сроком по всем складам ---

/** Пороги ОСГ списка риска, % (бэкенд принимает только их, иначе 422). */
export const EXPIRY_RISK_PCTS = [10, 20, 30] as const;
export type ExpiryRiskPct = (typeof EXPIRY_RISK_PCTS)[number];
export const DEFAULT_EXPIRY_RISK_PCT: ExpiryRiskPct = 20;

/** Партия риска на складе. `band` и `bucket` — бэкенда (по проценту и
 *  `bucket_for`), фронт их из дат не выводит. `days_left` < 0 — просрочено. */
export interface ExpiryRiskRow {
  organization_ref: string;
  organization_name: string;
  warehouse_ref: string;
  warehouse_name: string;
  nomenclature_ref: string;
  article: string;
  name: string;
  characteristic_ref: string;
  characteristic: string;
  batch_ref: string;
  batch: string;
  produced: string | null;
  expires: string;
  shelf_life_pct: number | null;
  bucket: string;
  band: ExpiryBand;
  days_left: number;
  qty: number;
  /** ₽ строк `Запасы` по ключу партии; `null` — строки нет (не ноль). */
  cost_rub: number | null;
}

export interface ExpiryBandCell {
  qty: number;
  /** ₽ только строк, у которых она есть; строки без ₽ названы в `warnings`. */
  cost_rub: number;
  positions: number;
}

/** Итог полосы по всем складам; полосы — до порога, нулевые тоже. */
export interface ExpiryBandTotal extends ExpiryBandCell {
  band: ExpiryBand;
  title: string;
}

export interface ExpiryWarehouse extends ExpiryBandCell {
  organization_ref: string;
  organization_name: string;
  warehouse_ref: string;
  warehouse_name: string;
  /** Только непустые полосы склада. */
  bands: Partial<Record<ExpiryBand, ExpiryBandCell>>;
}

/** Юрлицо, скрытое настройкой: его партии риска — итогом, не в списке. */
export interface ExpiryHidden extends ExpiryBandCell {
  ref: string;
  name: string;
}

export interface ExpiryRisk {
  as_of: string;
  today: string;
  max_pct: number;
  totals: ExpiryBandTotal[];
  by_warehouse: ExpiryWarehouse[];
  /** По `expires` возр. — порядок бэкенда. */
  rows: ExpiryRiskRow[];
  hidden: ExpiryHidden[];
  warnings: string[];
}

/** Ключ react-query списка риска (с порогом вторым элементом). */
export const EXPIRY_RISK_QUERY = "warehouse-dashboard-expiry-risk";

export async function getExpiryRisk(maxPct: ExpiryRiskPct): Promise<ExpiryRisk> {
  const { data } = await api.get<ExpiryRisk>("/warehouse-dashboard/expiry-risk", {
    params: { max_pct: maxPct },
  });
  return data;
}
